import { useState, useEffect } from "react";
import { FileWarning, Search, AlertTriangle, Info, AlertCircle, RefreshCw, Loader2, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface AuditLog {
  id: string;
  user_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: any;
  severity: string;
  created_at: string;
}

const severityConfig: Record<string, { icon: any; color: string; label: string }> = {
  info: { icon: Info, color: "bg-blue-500/10 text-blue-400 border-blue-500/20", label: "Info" },
  warning: { icon: AlertTriangle, color: "bg-warning/10 text-warning border-warning/20", label: "Aviso" },
  error: { icon: AlertCircle, color: "bg-destructive/10 text-destructive border-destructive/20", label: "Erro" },
  critical: { icon: FileWarning, color: "bg-red-600/10 text-red-400 border-red-600/20", label: "Crítico" },
};

const AdminAuditTab = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("all");

  const fetchLogs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("audit_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    setLogs((data as AuditLog[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const filteredLogs = logs.filter((log) => {
    const matchesSearch = search === "" ||
      log.action.toLowerCase().includes(search.toLowerCase()) ||
      (log.entity_type || "").toLowerCase().includes(search.toLowerCase()) ||
      JSON.stringify(log.details || {}).toLowerCase().includes(search.toLowerCase());
    const matchesSeverity = severityFilter === "all" || log.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  const severityCounts = {
    all: logs.length,
    info: logs.filter(l => l.severity === "info").length,
    warning: logs.filter(l => l.severity === "warning").length,
    error: logs.filter(l => l.severity === "error").length,
    critical: logs.filter(l => l.severity === "critical").length,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Auditoria & Logs</h2>
          <p className="text-xs text-muted-foreground">{logs.length} registros</p>
        </div>
        <Button onClick={fetchLogs} disabled={loading} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Severity counters */}
      <div className="grid grid-cols-5 gap-2">
        {(["all", "info", "warning", "error", "critical"] as const).map((sev) => (
          <button
            key={sev}
            onClick={() => setSeverityFilter(sev)}
            className={`rounded-lg border p-3 text-center transition-colors ${
              severityFilter === sev ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary/30"
            }`}
          >
            <p className="text-lg font-bold font-display">{severityCounts[sev]}</p>
            <p className="text-[10px] text-muted-foreground capitalize">
              {sev === "all" ? "Todos" : severityConfig[sev]?.label || sev}
            </p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar nos logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-card border-border"
        />
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filteredLogs.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
          <FileWarning className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>Nenhum log encontrado</p>
          <p className="text-[10px] mt-1">As ações do painel serão registradas automaticamente</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left p-4 font-medium w-10">Nível</th>
                  <th className="text-left p-4 font-medium">Ação</th>
                  <th className="text-left p-4 font-medium hidden md:table-cell">Entidade</th>
                  <th className="text-left p-4 font-medium hidden lg:table-cell">Detalhes</th>
                  <th className="text-left p-4 font-medium">Data</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => {
                  const sev = severityConfig[log.severity] || severityConfig.info;
                  const SevIcon = sev.icon;
                  return (
                    <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="p-4">
                        <Badge className={`text-[10px] border ${sev.color}`}>
                          <SevIcon className="h-3 w-3 mr-1" />
                          {sev.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-medium">{log.action.replace(/_/g, " ")}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{log.user_id?.slice(0, 8) || "sistema"}</p>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">
                        {log.entity_type && (
                          <div>
                            <span className="capitalize">{log.entity_type}</span>
                            {log.entity_id && <span className="text-[10px] font-mono ml-1">#{log.entity_id.slice(0, 8)}</span>}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-xs text-muted-foreground hidden lg:table-cell max-w-[200px] truncate">
                        {log.details && Object.keys(log.details).length > 0 ? JSON.stringify(log.details) : "—"}
                      </td>
                      <td className="p-4 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(log.created_at).toLocaleDateString("pt-BR")}{" "}
                        {new Date(log.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditTab;
